
import { useEffect } from "react";
import type { SubscriptionTiersProps } from "@/types/subscription";
import { subscriptionTiers } from "@/data/subscriptionTiers";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { SubscriptionTierCard } from "./SubscriptionTierCard";
import { usePayPalIntegration } from "./PayPalIntegration";

declare global {
  interface Window {
    paypal?: any;
  }
}

export const SubscriptionTiers = ({ onSubscriptionSelected }: SubscriptionTiersProps) => {
  const { toast } = useToast();
  const {
    isLoadingPayPal,
    scriptError,
    initializePayPalButton,
    handleRetry,
    setIsLoadingPayPal,
    setScriptError
  } = usePayPalIntegration({ 
    onLoadingChange: (loading) => setIsLoadingPayPal(loading),
    onScriptError: (error) => setScriptError(error),
    onSubscriptionSelected 
  });

  useEffect(() => {
    let mounted = true;
    let retryCount = 0;
    const maxRetries = 5;
    const retryDelay = 2000;

    const initializeButtons = async () => {
      if (!window.paypal || !mounted) {
        if (retryCount < maxRetries) {
          console.log(`PayPal not loaded yet, retry ${retryCount + 1}/${maxRetries}`);
          retryCount++;
          setTimeout(initializeButtons, retryDelay);
        }
        return;
      }

      try {
        console.log('Fetching PayPal client ID...');
        const { data, error } = await supabase.functions.invoke('get-paypal-client-id');
        if (error) throw error;

        console.log('Initializing PayPal buttons...');
        let buttonsInitialized = false;
        for (const tier of subscriptionTiers) {
          try {
            await initializePayPalButton(tier, data.value);
            buttonsInitialized = true;
            console.log(`PayPal button initialized for tier: ${tier.name}`);
          } catch (error) {
            console.error(`Failed to initialize PayPal button for tier ${tier.name}:`, error);
          }
        }

        if (!buttonsInitialized && retryCount < maxRetries) {
          retryCount++;
          console.log(`No buttons initialized, retry ${retryCount}/${maxRetries}`);
          setTimeout(initializeButtons, retryDelay);
          return;
        }

        if (!buttonsInitialized) {
          throw new Error('Failed to initialize PayPal buttons after multiple attempts');
        }

        setIsLoadingPayPal(false);
      } catch (error) {
        console.error('Error initializing PayPal:', error);
        setScriptError(true);
        toast({
          title: "Error",
          description: "Failed to load payment options. Please try again.",
          variant: "destructive",
        });
      }
    };

    const timer = setTimeout(() => {
      console.log('Starting PayPal initialization...');
      initializeButtons();
    }, 2000);

    return () => {
      mounted = false;
      clearTimeout(timer);
    };
  }, [initializePayPalButton, toast, setIsLoadingPayPal, setScriptError]);

  return (
    <div className="space-y-6 px-4 sm:px-0">
      <div className="text-center space-y-4">
        <h2 className="text-2xl sm:text-3xl font-bold">Choose Your Plan</h2>
        <p className="text-gray-600 text-sm sm:text-base max-w-2xl mx-auto">
          Select the perfect subscription tier to enhance your client communications with AI-powered content
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {subscriptionTiers.map((tier) => (
          <SubscriptionTierCard
            key={tier.name}
            tier={tier}
            isLoadingPayPal={isLoadingPayPal}
            scriptError={scriptError}
            onRetry={handleRetry}
          />
        ))}
      </div>
    </div>
  );
};
